import React, { createContext, useContext, useState, useEffect } from 'react';
import { productService } from '../services/api';
import { filterRealProducts } from '../utils/isDemoProduct';
import { normalizeProductPricesForDisplay } from '../utils/productPricing';

function mapProductsWithPricing(list) {
  if (!Array.isArray(list)) return [];
  return list.map((p) => {
    const prices = normalizeProductPricesForDisplay(p);
    return {
      ...p,
      price: prices.price,
      originalPrice: prices.originalPrice,
      salePrice: prices.salePrice,
      isOnSale: prices.isOnSale,
    };
  });
}

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load products from backend on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to load from backend first (paginate first page — creator page loads its own full list)
        const response = await productService.getProducts({ page: 1, limit: 100 });
        let normalizedProducts = [];
        if (response?.success && Array.isArray(response.products)) {
          normalizedProducts = response.products;
        } else if (Array.isArray(response?.data)) {
          normalizedProducts = response.data;
        } else if (Array.isArray(response)) {
          normalizedProducts = response;
        }

        if (normalizedProducts.length > 0) {
          setProducts(mapProductsWithPricing(filterRealProducts(normalizedProducts)));
        } else {
          const savedProducts = localStorage.getItem('sheenwear_products');
          if (savedProducts) {
            try {
              setProducts(mapProductsWithPricing(filterRealProducts(JSON.parse(savedProducts))));
            } catch (error) {
              console.error('Error loading products from localStorage:', error);
              setProducts([]);
            }
          } else {
            setProducts([]);
          }
        }
      } catch (error) {
        console.error('Error loading products from backend:', error);
        setError(error.message);

        const savedProducts = localStorage.getItem('sheenwear_products');
        if (savedProducts) {
          try {
            setProducts(mapProductsWithPricing(filterRealProducts(JSON.parse(savedProducts))));
          } catch (parseError) {
            console.error('Error loading products from localStorage:', parseError);
            setProducts([]);
          }
        } else {
          setProducts([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Save products to localStorage whenever products change (for offline support)
  useEffect(() => {
    if (products.length > 0) {
      localStorage.setItem('sheenwear_products', JSON.stringify(products));
    }
  }, [products]);

  // Update stock for a specific product, color, and size
  const updateStock = (productId, color, size, newQuantity) => {
    setProducts(prevProducts => 
      prevProducts.map(product => {
        if (product.id === productId) {
          let updatedStock;
          
          // For unstitched products, stock is color-based only
          if (product.category === 'unstitched') {
            updatedStock = {
              ...product.stock,
              [color]: Math.max(0, newQuantity)
            };
          } else {
            // For other products, stock is color-size based
            updatedStock = {
              ...product.stock,
              [color]: {
                ...product.stock[color],
                [size]: Math.max(0, newQuantity)
              }
            };
          }
          
          return {
            ...product,
            stock: updatedStock
          };
        }
        return product;
      })
    );
  };

  // Add new product
  const addProduct = async (newProduct) => {
    try {
      const response = await productService.createProduct(newProduct);
      if (response.product) {
        setProducts(prevProducts => [...prevProducts, response.product]);
        return { success: true, product: response.product };
      } else {
        throw new Error('Failed to create product');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      return { success: false, error: error.message };
    }
  };

  // Update existing product
  const updateProduct = async (productId, updatedProduct) => {
    try {
      const response = await productService.updateProduct(productId, updatedProduct);
      if (response.product) {
        setProducts(prevProducts => 
          prevProducts.map(product => 
            product.id === productId ? response.product : product
          )
        );
        return { success: true, product: response.product };
      } else {
        throw new Error('Failed to update product');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete product
  const deleteProduct = async (productId) => {
    try {
      await productService.deleteProduct(productId);
      setProducts(prevProducts => 
        prevProducts.filter(product => product.id !== productId)
      );
      return { success: true };
    } catch (error) {
      console.error('Error deleting product:', error);
      return { success: false, error: error.message };
    }
  };

  // Get total stock for a product
  const getTotalStock = (product) => {
    if (!product.stock) return 0;
    
    // For unstitched products, stock is color-based only
    if (product.category === 'unstitched') {
      return Object.values(product.stock).reduce((total, colorStock) => {
        return total + (colorStock || 0);
      }, 0);
    }
    
    // For other products, stock is color-size based
    return Object.values(product.stock).reduce((total, colorStock) => {
      return total + Object.values(colorStock).reduce((colorTotal, sizeStock) => {
        return colorTotal + (sizeStock || 0);
      }, 0);
    }, 0);
  };

  // Get stock for specific color and size
  const getStockForColorSize = (product, color, size) => {
    // For unstitched products, stock is color-based only
    if (product.category === 'unstitched') {
      if (!product.stock || !product.stock[color]) return 0;
      return product.stock[color] || 0;
    }
    
    // For other products, stock is color-size based
    if (!product.stock || !product.stock[color]) return 0;
    return product.stock[color][size] || 0;
  };

  // Check if product is out of stock
  const isOutOfStock = (product) => {
    return getTotalStock(product) === 0;
  };

  // Clear cached catalog (no static demo seed — reload from API on next mount/effect)
  const resetToInitialProducts = () => {
    setProducts([]);
    setError(null);
    localStorage.removeItem('sheenwear_products');
  };

  const value = {
    products,
    setProducts,
    loading,
    error,
    updateStock,
    addProduct,
    updateProduct,
    deleteProduct,
    getTotalStock,
    getStockForColorSize,
    isOutOfStock,
    resetToInitialProducts
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}; 